import "./style.scss";

import MessageBlock from "components/shared/MessageBlock";
import TermRecap from "components/TermRecap";
import type { IChannel } from "containers/Channels/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import type { TermGroup } from "store/beneficiaryStore";
import type { ChannelCompensationGroup } from "store/channelStore";
import type { TPeriod } from "store/periodStore";

import type { ChannelParams, ITermMessagesOptions } from "..";
import type { GroupParams } from "..";
import type { GroupWithChannels } from "../ChannelsStep";

interface RecapStep {
    /**
     * Index of the current step.
     */
    indexStep: number;
    /**
     * Title of the section.
     */
    stepTitle: string;
    /**
     * Subtitle of the section.
     */
    stepSubtitle: string;
    /**
     * All channels of the company.
     */
    channels: IChannel[];
    /**
     * (useState) array that contains channels chosen within the form and its data.
     */
    chosenChannels: ChannelParams[];
    /**
     * Function from React Hook Form to have all values from form.
     */
    getValues: any;
    chosenCompensation: number | string | undefined;
    channelGroups: ChannelCompensationGroup[];
    /**
     * Compensation groups with channels associated (and only those that have channels)
     */
    groupsWithChannels: GroupWithChannels[];
    /**
     * Whether company uses groups of channels for compensation.
     */
    usesCompensation: boolean;
    /**
     * Channel compensation groups chosen.
     */
    chosenGroups: GroupParams[];
    /**
     * Whether company uses groups of terms for avance compensation.
     */
    usesAdvanceCompensation: boolean;
    chosenAdvanceGroup: TermGroup | null | undefined;
    useFormData: any;
    createAdvanceInManagement: boolean | null;
    advancePaymentExists: boolean;
    /**
     * Needed to see if beneficiary has other terms and display warning.
     */
    otherTermsMessage: ITermMessagesOptions;
    /**
     * Does the company want to display initial advance (handles chip for linked advance payment)
     */
    usesInitialAdvance: boolean;
    /**
     * Whether the company uses cumulative royalties (controls visibility of "Droits cumulés").
     */
    usesCumulativeRoyalties: boolean;
    /**
     * Inline advance payment slices entered in the term form (amounts in cents).
     */
    inlineAdvancePayment: TAdvancePayment | null;
    mode: "new" | "edit";
    currentPeriod?: TPeriod;
}

/**
 * This component handles the Recap section of the Term form. It holds the TermRecap component that can (will?) also be used in the ShowDetails component.
 */
const RecapStep = ({
    indexStep,
    stepTitle,
    stepSubtitle,
    channels,
    chosenChannels,
    getValues,
    chosenCompensation,
    chosenGroups,
    usesCompensation,
    groupsWithChannels,
    usesAdvanceCompensation,
    chosenAdvanceGroup,
    useFormData,
    createAdvanceInManagement,
    advancePaymentExists,
    otherTermsMessage,
    usesInitialAdvance,
    usesCumulativeRoyalties,
    inlineAdvancePayment,
    mode,
    currentPeriod,
}: RecapStep) => {
    const { watch } = useFormData;
    const [t] = useTranslation();

    // Array with ids of channels that exist within a group.
    const groupWithChannelIds = groupsWithChannels.map((group) => group.channels.map((channel) => channel.id)).flat();

    // Channels that aren't inside a group:
    const filteredChannels = useMemo(() => {
        return usesCompensation
            ? (chosenChannels ?? []).filter((channel) => !groupWithChannelIds.includes(channel?.channelId))
            : chosenChannels ?? [];
    }, [chosenChannels]);

    // Channels that are inside a group:
    const groupChannels = useMemo(() => {
        return usesCompensation
            ? (chosenChannels ?? []).filter((channel) => groupWithChannelIds.includes(channel?.channelId))
            : chosenChannels ?? [];
    }, [chosenChannels, groupsWithChannels]);

    const settingsStepData = useMemo(
        () => ({
            reference: watch("reference"),
            contractTypology: watch("contractTypology"),
            initialProvision: watch("initialProvision"),
            initialAdvance: watch("initialAdvance"),
            endDate: watch("endDate"),
            cumulativeRights: watch("cumulativeRights"),
            isTitlePublishedBeforePeriod: watch("isTitlePublishedBeforePeriod"),
            titleReleaseDate: watch("titleReleaseDate"),
            currentPeriod,
        }),
        [
            watch([
                "reference",
                "contractTypology",
                "initialAdvance",
                "initialProvision",
                "endDate",
                "cumulativeRights",
                "isTitlePublishedBeforePeriod",
                "titleReleaseDate",
            ]),
            currentPeriod,
        ],
    );

    return (
        <>
            <h3 className="form-section-title">
                {" "}
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="summary-container">
                <TermRecap
                    data={getValues}
                    chosenChannels={filteredChannels}
                    channels={channels}
                    chosenCompensation={chosenCompensation}
                    usesGroups={usesCompensation}
                    chosenGroups={chosenGroups}
                    channelsInGroups={groupChannels}
                    usesAdvanceCompensation={usesAdvanceCompensation}
                    chosenAdvanceGroup={chosenAdvanceGroup}
                    settingsStepData={settingsStepData}
                    createAdvanceInManagement={!!createAdvanceInManagement}
                    advancePaymentExists={advancePaymentExists}
                    usesInitialAdvance={usesInitialAdvance}
                    usesCumulativeRoyalties={usesCumulativeRoyalties}
                    inlineAdvancePayment={inlineAdvancePayment}
                    mode={mode}
                    from="form"
                    coveredTitles={watch("coveredTitles")}
                />
            </div>
            {otherTermsMessage.payment && <MessageBlock text={t("ConnectTitle.TermAdditionWarning")} type="warning" />}
        </>
    );
};

export default RecapStep;
